import { createContext, useEffect, useReducer } from 'react';
import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import type { User } from '../types/user';
import LoginApi from 'src/api/LoginApi';
import { LOCAL_STORAGE_KEYS } from 'src/constants';
import { Logger } from 'src/utils/logger';

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User | null;
}

interface AuthContextValue extends State {
  platform: 'JWT';
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasImpersonateVendorAuth: () => boolean;
  hasBuyerOrCategoryManagerVendorPortalRole: () => boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    user: User | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: User;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type Action = InitializeAction | LoginAction | LogoutAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state: State, action: Action): State => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: 'JWT',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  hasImpersonateVendorAuth: () => false,
  hasBuyerOrCategoryManagerVendorPortalRole: () => false,
});

export const AuthProvider: FC<React.PropsWithChildren<AuthProviderProps>> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const accessToken = window.localStorage.getItem(LOCAL_STORAGE_KEYS.AccessToken);

        if (accessToken) {
          const user = await LoginApi.me();

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        Logger.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    const user: User = await LoginApi.login(username, password);

    console.log('login rest api', user);
    console.log('token', JSON.stringify(user.token));

    LoginApi.loginWebApi(username, password)
      .then(() => {
        console.log('login to WebApi Success');
      })
      .catch((err) => {
        console.error('login WebApi failed: ', err);
      });

    localStorage.setItem(LOCAL_STORAGE_KEYS.AccessToken, JSON.stringify(user.token));
    localStorage.setItem(LOCAL_STORAGE_KEYS.UserName, username);
    localStorage.setItem(LOCAL_STORAGE_KEYS.Password, password);
    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });
  };

  const logout = async (): Promise<void> => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.AccessToken);
    dispatch({ type: 'LOGOUT' });
  };

  const hasImpersonateVendorAuth = (): boolean => {
    const user = state.user;
    let hasImpersonateVendor = false;
    if (user) {
      let vendorPortalApp = user.applications?.find((app) => app.applicationName === 'Vendor Portal');
      if (vendorPortalApp) {
        vendorPortalApp.roles?.forEach((role) => {
          if (role.permissions?.find((permission) => permission.name === 'Impersonate Vendor')) {
            hasImpersonateVendor = true;
          }
        });
      }
    }

    return hasImpersonateVendor;
  };

  const hasBuyerOrCategoryManagerVendorPortalRole = (): boolean => {
    const user = state.user;
    const roleNames = new Set(['Buyer', 'Category Manager']);
    const vendorPortalApp = user?.applications?.find((app) => app.applicationName === 'Vendor Portal');
    if (!vendorPortalApp?.roles) {
      return false;
    }

    return vendorPortalApp.roles.some((r) => r.name != null && roleNames.has(r.name));
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        login,
        logout,
        hasImpersonateVendorAuth,
        hasBuyerOrCategoryManagerVendorPortalRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
